import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TIMESTAMP_STRING } from '../../../environments/constants';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { sha256 } from "js-sha256";
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem
} from 'src/app/utils/browser-storage';

const SWARM_API = '/proxy/api/v0/';
@Injectable({ providedIn: 'root' })
export class AuthService {
    public anonymousLoggedIn;
    public anonymousLoggedInSubject: BehaviorSubject<{}>;
    public anonymousLoggedInObserver: Observable<{}>;

    constructor(private http: HttpClient, public router: Router) {
        this.anonymousLoggedInSubject = new BehaviorSubject<{}>(this.anonymousLoggedIn);
        this.anonymousLoggedInObserver = this.anonymousLoggedInSubject.asObservable();
    }

    activateAutoLogin(email: String): Observable<String> {
        return this.http.get<String>(SWARM_API + 
            environment.server.endpoints.userService.users.activateAutoLogin + "/" + email,
            { responseType: 'text' as 'json' })
                .pipe(catchError(() => { return of('activation_failed') }));
    }

    loginWithCode(authorization_code: string, verifier: string): Observable<number> {
        return this.http.post<{ success: number, message: string }>(SWARM_API + `userservice/users/login`, {
            code: authorization_code,
            grant_type: 'authorization_code',
            redirect_uri: window.location.origin + '/login',
            scope: 'read',
            code_verifier: verifier
        }).pipe(map((res) => { 
            if (res.success) {
                removeLocalStorageItem('access_token');
                removeLocalStorageItem('access_token' + TIMESTAMP_STRING);
            }
            return res.success;
         }));
    }

    loginAnonymous(): void {
        this.http
            .post<{ access_token: string }>(SWARM_API + `userservice/users/login/anonymous`, {})
            .subscribe((token) => {
                let isUserLoggedIn: string = getLocalStorageItem('refresh_token');
                if (token && !isUserLoggedIn) {
                    setLocalStorageItem('access_token', token.access_token);
                    setLocalStorageItem('access_token' + TIMESTAMP_STRING, new Date().toString());
                    this.anonymousLoggedInSubject.next(token);
                }
            });
    }

    logout() {
        document.cookie = 'JSESSIONID=;max-age=0';
        this.http.get(SWARM_API + 'logout', { observe: 'response' }).pipe(
            map(res => {
                if(res.status == 200) {
                    window.location.href = '/sso/logout';
                    this.loginAnonymous(); 
                }
            })
        ).subscribe();
    }

    deleteCookie(name: String) {
        document.cookie =
            name +
            '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Max-Age=0';
    }

    getLoginPage() {
        let params: HttpParams = new HttpParams();
        params = params.append('client_id', 'swarm-frontend');
        params = params.set('response_type', 'code');
        params = params.set('scope', 'read');
        return this.http.get(SWARM_API + 'pcssoservice/oauth2/authorize', {
            params: params,
            responseType: 'text'
        });
    }

    registerSsoClient(clientRegistrationDto: {
        client: {
            clientId: string;
            clientName: string;
            clientSecret: string;
            authorizedGrantTypes: string;
            webServerRedirectUri: string;
        };
        productId: number;
    }) {
        var httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };
        return this.http.post(
            environment.oauthRegisterApp,
            clientRegistrationDto,
            httpOptions
        );
    }

    getClientInfo(ssoClientId: string) {
        return this.http.get<{
            clientId: string;
            clientName: string;
            clientSecret: string;
            authorizedGrantTypes: string;
            webServerRedirectUri: string;
        }>(SWARM_API + 'pcssoservice/oauth/client/info/' + ssoClientId);
    }

    async checkIfLogedin(): Promise<boolean> {
        try {
            let { verifier, challenge } = await this.createPkce();
            const authResponse = await fetch(this.getOauthLoginUriWithPkce(challenge), {
                method: "GET",
                credentials: "include",
                redirect: "follow"
            });

            if (authResponse.url.includes("login?code")) {
                const authCode = authResponse.url.split("code=")[1];
                const loginExpiresData = await this.loginWithCode(authCode, verifier).toPromise();
                if (!authCode) { return false; }
                return Boolean(loginExpiresData);
                // location.href = authResponse.url;
            }
        } catch (error) {
            return false;
        }
    }

    async login(redirect: boolean, username: string, password: string): Promise<'success' | 'invalid_credentials' | 'error' | { code: string, verifier: string }> {
        try {
            const loginResponse = await fetch("/sso/login", {
                method: "POST",
                credentials: "include",
                redirect: "manual",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({ username, password })
            });
            if(document.cookie.includes("login-response-status=bad-credentials")) {
                 return 'invalid_credentials';
            }

            let { verifier, challenge } = await this.createPkce();
            const authResponse = await fetch(this.getOauthLoginUriWithPkce(challenge), {
                method: "GET",
                credentials: "include",
                redirect: "follow"
            });

            if (authResponse.url.includes("code=")) {
                const authCode = authResponse.url.split("code=")[1];

                if(redirect) {
                    const swarmId = await this.loginWithCode(authCode, verifier).toPromise();
                    if (swarmId) {
                        return 'success';
                    } else {
                        return 'error';
                    }
                }
                else return { code: authCode, verifier: verifier };
            } else {
                return 'error';
            }
        } catch (error) {
            console.error("Login failed: ", error);
            return 'error';
        }
    }

    getPermission() {
    this.anonymousLoggedInSubject
    .pipe(take(1))
    .subscribe({
        next: (token) => {
        this.http
            .get(SWARM_API + 'userservice/users/anon/permissions')
            .subscribe({
            next: (permissions) => {
            },
            error: (err) => {
            },
            });
        },
    });
    }


    // Convert to base64 for secure environment
    base64urlEncode(a: ArrayBuffer): string {
        const bytes = new Uint8Array(a);
        let str = "";
        for (let i = 0; i < bytes.byteLength; ++i) str += String.fromCharCode(bytes[i]);
        return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    }

    // Convert to base64 for insecure environment
    base64urlEncodeBytes(bytes: Uint8Array): string {
        let str = "";
        for (let i = 0; i < bytes.byteLength; ++i) str += String.fromCharCode(bytes[i]);
        return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    }


    // Encode verifier to generate challenge with SHA256 algorithm
    async sha256(buffer: string): Promise<string> {
        // Check if the environment is secure (https and localhost)
        if(window.crypto?.subtle && window.isSecureContext) {
            const encoder = new TextEncoder();
            const data = encoder.encode(buffer);
            const digest = await crypto.subtle.digest("SHA-256", data);
            return this.base64urlEncode(digest);
        }
        // Else use insecure environment
        else {
            const hashBytes = new Uint8Array(sha256.arrayBuffer(buffer));
            return this.base64urlEncodeBytes(hashBytes);
        }
    }

    // Create challenge and verifier
    async createPkce() {
        const randomBytes = crypto.getRandomValues(new Uint8Array(64));
        const verifier = this.base64urlEncode(randomBytes.buffer);
        const challenge = await this.sha256(verifier);
        return { verifier, challenge };
    }

    getOauthLoginUriWithPkce(challenge: string): string {
        return `${environment.oauthLoginUri}&code_challenge=${challenge}&code_challenge_method=S256`;
    }

}
